/**
 * Call Contacts Dialog — shows contact phone numbers for users related to a study.
 *
 * Calls backend `getStudyContacts` which assembles contacts from:
 *   - Referring physician (study.phy)
 *   - Radiologist (study.radiologist)
 *   - Ordering location (study.service)
 *   - Contact fields (study.contact.contact1, contact2, location)
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, CircularProgress, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import type { Study, CallContact } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';

interface CallContactsDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const CallContactsDialog = ({ open, study, onClose }: CallContactsDialogProps) => {
  const [contacts, setContacts] = useState<CallContact[]>([]);
  const [loading, setLoading] = useState(false);

  const loadContacts = useCallback(async () => {
    if (!study) return;
    setLoading(true);
    try {
      const result = await api.getStudyContacts(study.objKey);
      setContacts(result.records);
    } catch (e) {
      pushError('Failed to load contacts: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, [study]);

  useEffect(() => {
    if (open && study) {
      loadContacts();
    }
  }, [open, study, loadContacts]);

  const formatPhone = (phone?: string) => {
    if (!phone) return '—';
    return phone;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Call Contacts{study ? ` — ${study.name}` : ''}</span>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadContacts} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {/* Study info summary */}
        {study && (
          <Box sx={{ mb: 2, p: 1, backgroundColor: 'action.hover', borderRadius: '4px' }}>
            <Typography sx={{ fontSize: 12 }}>
              <strong>Patient:</strong> {study.name} &nbsp;|&nbsp;
              <strong>Accession:</strong> {study.acc} &nbsp;|&nbsp;
              <strong>Physician:</strong> {study.physician ?? study.phyName ?? '—'}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : contacts.length === 0 ? (
          <Typography sx={{ py: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
            No contacts found for this study
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Office Phone</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Mobile Phone</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((c, i) => (
                  <TableRow key={c.objKey ?? i} hover>
                    <TableCell sx={{ fontSize: 12 }}>{c.name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{c.role}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {c.officePhone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <a href={`tel:${c.officePhone}`} style={{ color: 'inherit' }}>
                            {formatPhone(c.officePhone)}
                          </a>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {c.mobilePhone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <a href={`tel:${c.mobilePhone}`} style={{ color: 'inherit' }}>
                            {formatPhone(c.mobilePhone)}
                          </a>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{c.location ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
